import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BaseApiService } from './base-api.service';
import { EventBus } from './event.bus';
import { LocalStorage } from './local.storage';
import { AccountService } from './account.service';
import { PromisesService } from './promises.service';
import { ILoginRequestModel, ILoginResponseModel } from '../../utils/interfaces';
import { EventTypeEnum, ExternalSsoProvidersEnum, RequestTypeEnum, ToastrActionEnum } from '../../utils/enums';
import { SecurityHelper, StringHelper } from '../../utils/helpers';
import { environment } from 'src/environments/environment';
import { getProductName } from 'src/app/core/common';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { floatingMenuList } from '../../../models/AsgardFeatures';
import { VersionService } from '../version.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrDialogService } from '../toastr-dialog.service';
import { setSpeechEngine, speechEngineFromTtsVariant } from 'src/app/core/speech-engine';
import { CloudConnectionService } from '../cloud-connection/cloud-connection.service';
import { CloudServiceEnum } from '../cloud-storage/enums';
import { StiAuthService } from '../sti/sti-auth.service';

const TTS_VARIANT_CLAIM = 'tts_variant';
const AUTH_INFO_KEY = 'AUTH_INFO';
const ACCOUNT_INFO_KEY = 'ACCOUNT_INFO';
const CODE_VERIFIER_KEY = 'CODE_VERIFIER';
const DROPBOX_INFO_KEY = 'DROPBOX_INFO_KEY';
const SSO_PROVIDER_KEY = 'SSO_PROVIDER';
const REFRESH_TOKEN_KEY = 'REFRESH_TOKEN';
const ASGARD_SESSION_KEY = 'ASGARD_SESSION';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseApiService {
  private refreshingTimerID: number | null = null;

  private get baseUrl(): string {
    return environment.auth.apiUrl;
  }

  constructor(
    http: HttpClient,
    private router: Router,
    private eventBus: EventBus,
    private localStorage: LocalStorage,
    private accountService: AccountService,
    private promisesService: PromisesService,
    private uiService: CurrentUiStoreService,
    private versionService: VersionService,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrDialogService,
    private readonly cloudConnectionService: CloudConnectionService,
    private readonly stiAuthService: StiAuthService,
  ) {
    super(http);

    this.eventBus.on(EventTypeEnum.HTTP_ERROR).subscribe(async event => {
      if (event.data.status === 401) {
        await this.logout();
      }
    });
  }

  private get defaultHeaders(): { [key: string]: string } {
    return { 'Content-Type': 'application/x-www-form-urlencoded' };
  }

  private applySpeechEngine(authInfo: ILoginResponseModel): void {
    const variant = SecurityHelper.getTokenClaim(authInfo?.access_token, TTS_VARIANT_CLAIM);
    setSpeechEngine(speechEngineFromTtsVariant(variant));
  }

  public async refreshToken(): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('Unauthenticated');
    }

    const authInfo = this.getAuthInfo();
    await this.checkNewVersion();
    if (authInfo) {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append('refresh_token', authInfo.refresh_token);
      urlSearchParams.append('client_id', authInfo.client_id);
      urlSearchParams.append('grant_type', 'refresh_token');
      const body = urlSearchParams.toString();

      const result = await this.promisesService.share<ILoginResponseModel>(REFRESH_TOKEN_KEY, () => this.post<ILoginResponseModel>(
        `${this.baseUrl}/connect/token?product=${getProductName()}&device=${environment.auth.device}`,
        body,
        { headers: this.defaultHeaders }
      ));

      result.client_id = authInfo.client_id;
      // this.eventBus.emit(EventTypeEnum.TOKEN_REFRESHED);
      this.localStorage.set<ILoginResponseModel>(AUTH_INFO_KEY, result);
      this.applySpeechEngine(result);
      this.accountService.clear();
      await this.accountService.getAccountInfo();
      this.scheduleTokenRefresh();
    }
  }

  public getAuthInfo(): ILoginResponseModel | null {
    return this.localStorage.get<ILoginResponseModel>(AUTH_INFO_KEY);
  }

  public isAuthenticated(): boolean {
    return !!this.getAuthInfo();
  }

  public async logout(): Promise<void> {
    if (this.refreshingTimerID !== null) {
      clearTimeout(this.refreshingTimerID);
    }

    const authInfo = this.getAuthInfo();

    // Clear cloud connections first, while ACCOUNT_INFO_KEY is still in localStorage
    // so getKey() can correctly resolve the user-scoped storage key
    this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.OneDrive);
    this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.GoogleDrive);
    this.stiAuthService.logout();

    this.localStorage.remove(AUTH_INFO_KEY);
    this.localStorage.remove(ACCOUNT_INFO_KEY);
    this.localStorage.remove(DROPBOX_INFO_KEY);
    setSpeechEngine(null);

    if (authInfo) {
      await this.revokeAccess(authInfo);
    }

    this.router.navigate(['/login']);

    // deactivate current active floating menus upon logging out
    // do this after navigating, otherwise there's a visual effect of deactivating the active menus
    this.uiService.setItemUnActive(floatingMenuList);

    this.eventBus.emit(EventTypeEnum.LOGOUT);

    if (authInfo && authInfo.leer_id_id_token) {
      this.endSessionLeerId(authInfo.leer_id_id_token);
    }
  }

  private async revokeAccess(authInfo: ILoginResponseModel): Promise<void> {
    if (!authInfo) {
      return;
    }

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('token', authInfo.refresh_token);
    urlSearchParams.append('token_type_hint', 'refresh_token');
    urlSearchParams.append('client_id', authInfo.client_id);
    const body = urlSearchParams.toString();

    try {
      await this.post<ILoginResponseModel>(`${this.baseUrl}/connect/revocation`, body, { headers: this.defaultHeaders });
    } catch (e) {
      // suppress error
    }
  }

  private endSessionLeerId(leerIdIdToken: string): void {
    document.location.href = `${environment.auth.leerIdUrl}/v1/logout?${this.toQueryParams({
      id_token_hint: leerIdIdToken,
      post_logout_redirect_uri: `${this.baseUrl}/logout-leerid`,
      state: btoa(`clientUrl=${window.location.origin}/login`)
    })}`;
  }

  public async login(model: ILoginRequestModel, killSessionId: number | null = null): Promise<void> {
    const oldAuthInfo = this.getAuthInfo();
    if (oldAuthInfo) {
      await this.revokeAccess(oldAuthInfo);
    }

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('username', model.username);
    urlSearchParams.append('password', model.password);
    urlSearchParams.append('client_id', environment.auth.clientId);
    urlSearchParams.append('grant_type', 'password');
    const body = urlSearchParams.toString();

    if (!killSessionId) {
      killSessionId = null;
    }

    const result = await this.post<ILoginResponseModel>(
      `${this.baseUrl}/connect/token?product=${getProductName()}&device=${environment.auth.device}&killSessionId=${killSessionId}`,
      body,
      { headers: this.defaultHeaders },
      RequestTypeEnum.Login
    ) as ILoginResponseModel;

    result.client_id = environment.auth.clientId;
    this.localStorage.set<ILoginResponseModel>(AUTH_INFO_KEY, result);
    this.applySpeechEngine(result);
    this.accountService.clear();
    this.scheduleTokenRefresh();
    this.onSessionStart();
  }

  public async loginByCode(code: string, redirectUri?: string): Promise<void> {
    if (this.refreshingTimerID !== null) {
      clearTimeout(this.refreshingTimerID);
    }

    const codeVerifier = this.localStorage.get<string>(CODE_VERIFIER_KEY);
    const provider = this.localStorage.get<string>(SSO_PROVIDER_KEY);
    let leeridProviderParam = '';
    if (provider === ExternalSsoProvidersEnum.LeerIdAlinea || provider === ExternalSsoProvidersEnum.LeerIdK3000) {
      leeridProviderParam = `&provider=${provider.split(':')[1]}`;
    }

    const result = await this.get<ILoginResponseModel>(
      // tslint:disable-next-line:max-line-length
      `${this.baseUrl}/api/v1/auth/token?code=${code}&product=${getProductName()}&device=${environment.auth.device}&codeVerifier=${codeVerifier}${leeridProviderParam}&redirectUri=${redirectUri || this.redirectUri}`,
      null,
      RequestTypeEnum.LoginByCode
    );

    result.client_id = environment.auth.codeClientId;
    this.localStorage.remove(CODE_VERIFIER_KEY);
    this.localStorage.remove(SSO_PROVIDER_KEY);
    this.localStorage.set<ILoginResponseModel>(AUTH_INFO_KEY, result);
    this.applySpeechEngine(result);
    this.accountService.clear();
    this.eventBus.emit(EventTypeEnum.LOGIN);
    this.scheduleTokenRefresh();
    this.onSessionStart();
  }

  private get redirectUri(): string {
    return encodeURIComponent(`${document.location.origin}${document.location.pathname}`.replace(/\/$/, ''));
  }

  private async getUserId(): Promise<string | null> {
    if (this.isAuthenticated()) {
      const account = await this.accountService.getAccountInfo();
      if (account) {
        return account.id;
      }
    }

    return null;
  }

  private getChallenge(): { code: string, method: string, nonce: string, state: string } {
    const verifier = StringHelper.toBase64URLEncode(SecurityHelper.getRandomBytes(32).toString('base64'));
    this.localStorage.set<string>(CODE_VERIFIER_KEY, verifier);
    const code = StringHelper.toBase64URLEncode(SecurityHelper.sha256(verifier));
    const method = 'S256';
    const nonce = StringHelper.random(16);
    const state = StringHelper.random(16);

    return {
      nonce,
      state,
      code,
      method
    };
  }

  public async externalLogin(silent: boolean, provider: ExternalSsoProvidersEnum,
    redirectUri: string | null = null, killSessionId: number | null = null): Promise<void> {
    if (!silent) {
      const oldAuthInfo = this.getAuthInfo();
      if (oldAuthInfo) {
        await this.revokeAccess(oldAuthInfo);
        if (killSessionId) {
          this.localStorage.remove(AUTH_INFO_KEY);
          if (this.refreshingTimerID !== null) {
            clearTimeout(this.refreshingTimerID);
          }
        }
      }
    }

    if (provider === ExternalSsoProvidersEnum.LeerIdAlinea || provider === ExternalSsoProvidersEnum.LeerIdK3000) {
      this.localStorage.set(SSO_PROVIDER_KEY, provider);
    }

    const userId = await this.getUserId();
    const challenge = this.getChallenge();
    // if (!killSessionId) {
    //   killSessionId = null;
    // }
    // tslint:disable-next-line:max-line-length
    document.location.href = `${this.baseUrl}/connect/authorize?client_id=${environment.auth.codeClientId}&redirect_uri=${redirectUri || this.redirectUri}&response_type=code&product=${getProductName()}&scope=openid%20offline_access&acr_values=${encodeURIComponent(provider)}&state=${challenge.state}&nonce=${challenge.nonce}&userId=${userId}&code_challenge=${encodeURIComponent(challenge.code)}&code_challenge_method=${challenge.method}&killSessionId=${killSessionId}`;
  }

  /** Called on every new login. Ensures OneDrive/Google Drive tokens are always fresh per session. */
  private onSessionStart(): void {
    this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.OneDrive);
    this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.GoogleDrive);
  }

  /**
   * Called on app init. Clears stale cloud connections when the browser is reopened
   * without a prior login in this tab. Skips clearing if the teacher is already
   * authenticated (e.g. a new tab opened mid-session) to avoid disrupting an active
   * cloud connection on other tabs.
   */
  public clearCloudIfNewBrowserSession(): void {
    if (!sessionStorage.getItem(ASGARD_SESSION_KEY) && !this.isAuthenticated()) {
      this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.OneDrive);
      this.cloudConnectionService.clearDriveInfo(CloudServiceEnum.GoogleDrive);
    }
    sessionStorage.setItem(ASGARD_SESSION_KEY, 'active');
  }

  private scheduleTokenRefresh(): void {
    const authInfo = this.getAuthInfo();

    if (!authInfo) {
      return;
    }

    if (this.refreshingTimerID !== null) {
      clearTimeout(this.refreshingTimerID);
    }

    console.log('Atorefresh token were scheduled');

    this.refreshingTimerID = setTimeout((() => {
      console.log('Atorefresh token were executed');
      this.refreshToken();
    }) as TimerHandler, (authInfo.expires_in - 60) * 1000);
  }

  async isSameUser(userId: string): Promise<boolean> {
    return userId === await this.getUserId();
  }

  private isTokenValid(token: string): boolean {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = JSON.parse(atob(base64));

    if (!jsonPayload || !jsonPayload.hasOwnProperty('exp')) {
      throw Error('Unable to validate provided token.');
    }

    return new Date() < new Date(jsonPayload.exp * 1000);
  }

  public async getToken(refreshIfneed: boolean = true): Promise<string> {
    const jwtToken = this.getAuthInfo()?.access_token;

    if (!jwtToken) {
      throw Error('Unable to get token.');
    }

    if (refreshIfneed && !this.isTokenValid(jwtToken)) {
      await this.refreshToken();
    }

    return this.getAuthInfo()?.access_token || '';
  }

  private async checkNewVersion(): Promise<void> {
    const isNewVersionExists = await this.checkNewVersionExists();
    if (isNewVersionExists) {
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant('RELOAD_MESSAGE'), '', true);
    }
  }

  private async checkNewVersionExists(): Promise<boolean> {
    let isNewVersionExists = false;

    try {
      const build = environment.build;
      if (build) {
        const buildMatch = build.match(/build (\d+)/);
        if (buildMatch && buildMatch[1]) {
          const buildNum = +buildMatch[1];

          const currentBuildNum = await this.versionService.getCurrentVersion();
          isNewVersionExists = currentBuildNum > buildNum;
        }
      }
    } catch {
      isNewVersionExists = false;
    }

    return isNewVersionExists;
  }
}
